import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lightning, GoogleLogo, Envelope, Lock, Flame } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardPathFor } from "@/lib/roles";
import { Input } from "@/components/ui/input";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/post-login";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Athletic hero image — barbell / lifter, high energy dark tones
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?auto=format&fit=crop&w=1600&q=80";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back, soldier.");
      navigate(dashboardPathFor(u?.primary_role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-surface-base" data-testid="login-page">
      {/* LEFT: hero visual */}
      <div
        className="relative hidden lg:block lg:col-span-3 overflow-hidden landing-dark"
        data-testid="login-hero-panel"
      >
        <img
          src={HERO_IMAGE}
          alt="Athletic training"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Deep royal-blue tint + heat glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(15,23,42,0.72) 0%, rgba(30,58,138,0.60) 55%, rgba(255,107,53,0.30) 100%)",
          }}
          aria-hidden
        />
        {/* Diagonal stripe accent */}
        <div
          className="absolute inset-0 mix-blend-overlay opacity-30 stripe-bg pointer-events-none"
          aria-hidden
        />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between p-10 lg:p-14 text-white">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Edge Training" className="h-12 w-12 rounded-lg shadow-lg" />
            <span className="font-heading text-xl tracking-tight leading-none text-white">EDGE TRAINING</span>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="energy-dot"></span>
              <span className="label-tiny text-white/90">// HIGH PERFORMANCE PLATFORM</span>
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6">
              TRAIN <span className="text-[#F0D000]">HARDER.</span><br />
              RECOVER <span className="text-[#F0D000]">SMARTER.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              The coaching and training command center athletes trust — programs, form review,
              streaks, and PR tracking, all in one place.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-lg">
            {[
              { k: "SESSIONS", v: "10K+" },
              { k: "ATHLETES", v: "1.2K" },
              { k: "PRS SET", v: "3.5K" },
            ].map((s) => (
              <div key={s.k} className="border-l-2 border-[#F0D000] pl-3">
                <div className="font-heading text-3xl tabular-nums">{s.v}</div>
                <div className="label-tiny text-white/70 text-[10px]">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT: auth card */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-10 stripe-bg">
        <div className="w-full max-w-md tactical-card p-10 relative">
          <Link
            to="/"
            className="flex items-center gap-2 mb-10 lg:hidden"
            data-testid="login-brand-link"
          >
            <img src="/icon-192.png" alt="Edge Training" className="h-10 w-10 rounded-lg" />
            <span className="font-heading text-lg tracking-tight leading-none text-white">EDGE TRAINING</span>
          </Link>

          <div className="mb-8">
            <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
              <Flame size={12} weight="fill" className="text-heat" /> // LET&apos;S GO
            </div>
            <h1 className="font-heading text-4xl mb-2">SIGN IN</h1>
            <p className="text-sm text-text-secondary">Resume your training command center.</p>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full mb-6 border border-white/10 hover:border-white/40 hover:bg-white/5 transition-colors px-4 py-3 flex items-center justify-center gap-3"
            data-testid="login-google-button"
          >
            <GoogleLogo size={20} weight="bold" />
            <span className="label-tiny">CONTINUE WITH GOOGLE</span>
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label-tiny text-text-secondary">OR</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-tiny block mb-2">EMAIL</label>
              <div className="relative">
                <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-surface-elevated border-white/10 focus:border-brand-accent focus-visible:ring-1 focus-visible:ring-brand-accent text-white"
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <label className="label-tiny block mb-2">PASSWORD</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-surface-elevated border-white/10 focus:border-brand-accent focus-visible:ring-1 focus-visible:ring-brand-accent text-white"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "AUTHENTICATING..." : "LOG IN"}
            </button>
          </form>

          <p className="text-sm text-text-secondary text-center mt-6">
            No account yet?{" "}
            <Link
              to="/register"
              className="text-brand-accent hover:underline font-medium"
              data-testid="login-to-register-link"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
